import type { ObservabilityUserAccessItem } from '@scatterin/api-types';
import { Clock3, ShieldCheck, ShieldX, UserRoundCheck, UserRoundX } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/confirmDialog';
import { SearchInput } from '@/components/filterChips';
import { EmptyState, ErrorState, LoadingState } from '@/components/stateViews';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableShell } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useUserAccessQuery, useUserAccessUpdateMutation } from '@/hooks/useObservabilityQueries';
import { useRouteContext } from '@/hooks/useRouteContext';
import { formatRelativeTime, formatTime } from '@/lib/format';
import { matchesSearch } from '@/lib/search';

type AccessState = 'approved' | 'disabled' | 'pending';
type AccessView = AccessState | 'all';

const accessViews = ['pending', 'approved', 'disabled', 'all'] as const;

const currentAccessView = (search: string): AccessView => {
  const view = new URLSearchParams(search).get('access');
  return accessViews.includes(view as AccessView) ? (view as AccessView) : 'pending';
};

const updateAccessView = (current: string, view: AccessView): string => {
  const search = new URLSearchParams(current);
  if (view === 'pending') search.delete('access');
  else search.set('access', view);
  return search.toString();
};

const accessState = (user: ObservabilityUserAccessItem): AccessState => {
  if (!user.enabled) return 'disabled';
  if (user.userStatus?.toUpperCase() === 'UNCONFIRMED') return 'pending';
  return user.saasAccess ? 'approved' : 'disabled';
};

const accessLabel = (state: AccessView): string => {
  if (state === 'pending') return 'Pending approval';
  if (state === 'approved') return 'Approved';
  if (state === 'all') return 'All';
  return 'Disabled';
};

const accessTone = (state: AccessState): 'amber' | 'green' | 'slate' => {
  if (state === 'pending') return 'amber';
  if (state === 'approved') return 'green';
  return 'slate';
};

const displayIdentity = (user: ObservabilityUserAccessItem): string => user.email || user.username;

const accessChangeDescription = (user: ObservabilityUserAccessItem, saasAccess: boolean, environment: string): string => {
  const identity = displayIdentity(user);
  if (saasAccess) return `${identity} will be confirmed if needed and gain SaaS platform access in ${environment}.`;
  return `${identity} will lose SaaS platform access in ${environment}. Their analytics-operator access is unchanged.`;
};

const TechnicalIdentity = ({ user }: { user: ObservabilityUserAccessItem }) => (
  <details className="border-line min-w-0 rounded-md border">
    <summary className="text-muted cursor-pointer px-3 py-2 text-xs font-semibold">Technical identity</summary>
    <dl className="border-line grid gap-2 border-t px-3 py-2 text-xs">
      <div>
        <dt className="text-muted">Cognito username</dt>
        <dd className="text-ink mt-1 font-mono break-all">{user.username}</dd>
      </div>
      <div>
        <dt className="text-muted">Cognito subject</dt>
        <dd className="text-ink mt-1 font-mono break-all">{user.sub || 'Not returned'}</dd>
      </div>
      <div>
        <dt className="text-muted">Account status</dt>
        <dd className="text-ink mt-1">
          {user.userStatus || 'Not returned'} · {user.enabled ? 'enabled' : 'disabled'}
        </dd>
      </div>
      <div>
        <dt className="text-muted">Created</dt>
        <dd className="text-ink mt-1">{formatTime(user.createdAt)}</dd>
      </div>
    </dl>
  </details>
);

const AccessAction = ({
  isPending,
  onSelect,
  user,
}: {
  isPending: boolean;
  onSelect: (user: ObservabilityUserAccessItem, saasAccess: boolean) => void;
  user: ObservabilityUserAccessItem;
}) => {
  const state = accessState(user);
  if (!user.enabled) return <span className="text-muted text-xs">Cognito account is disabled</span>;
  if (state === 'approved') {
    return (
      <Button disabled={isPending} onClick={() => onSelect(user, false)} variant="danger">
        <ShieldX aria-hidden="true" className="size-4" />
        Disable access
      </Button>
    );
  }
  return (
    <Button disabled={isPending} onClick={() => onSelect(user, true)}>
      <ShieldCheck aria-hidden="true" className="size-4" />
      Approve access
    </Button>
  );
};

export const UsersPage = () => {
  const { setState, state } = useRouteContext();
  const usersQuery = useUserAccessQuery(state);
  const updateMutation = useUserAccessUpdateMutation();
  const location = useLocation();
  const navigate = useNavigate();
  const view = currentAccessView(location.search);
  const [pendingAccessChange, setPendingAccessChange] = useState<{ saasAccess: boolean; user: ObservabilityUserAccessItem } | null>(null);

  if (usersQuery.isLoading) return <LoadingState label={`Loading ${state.env} users`} />;
  if (usersQuery.isError) return <ErrorState message={usersQuery.error.message} onRetry={() => void usersQuery.refetch()} />;

  const allUsers = usersQuery.data?.users ?? [];
  const counts = {
    all: allUsers.length,
    approved: allUsers.filter((user) => accessState(user) === 'approved').length,
    disabled: allUsers.filter((user) => accessState(user) === 'disabled').length,
    pending: allUsers.filter((user) => accessState(user) === 'pending').length,
  };
  const users = allUsers
    .filter((user) => view === 'all' || accessState(user) === view)
    .filter((user) => matchesSearch(state.search, [user.username, user.email, user.sub, user.userStatus]));

  const setAccess = (user: ObservabilityUserAccessItem, saasAccess: boolean) => {
    updateMutation.mutate(
      { environment: state.env, saasAccess, username: user.username },
      {
        onError: (error) => toast.error(error instanceof Error ? error.message : 'Failed to update user access'),
        onSuccess: () => {
          setPendingAccessChange(null);
          toast.success(saasAccess ? 'SaaS access approved' : 'SaaS access disabled');
        },
      },
    );
  };

  return (
    <div className="grid min-w-0 gap-5">
      <ConfirmDialog
        confirmLabel={pendingAccessChange?.saasAccess ? 'Approve access' : 'Disable access'}
        description={
          pendingAccessChange ? accessChangeDescription(pendingAccessChange.user, pendingAccessChange.saasAccess, state.env) : undefined
        }
        isPending={updateMutation.isPending}
        onConfirm={() => {
          if (pendingAccessChange) setAccess(pendingAccessChange.user, pendingAccessChange.saasAccess);
        }}
        onOpenChange={(open) => {
          if (!open) setPendingAccessChange(null);
        }}
        open={Boolean(pendingAccessChange)}
        title={pendingAccessChange?.saasAccess ? 'Approve SaaS access' : 'Disable SaaS access'}
        variant={pendingAccessChange?.saasAccess ? 'primary' : 'danger'}
      />

      <section className="grid gap-3 md:grid-cols-[minmax(0,1.4fr)_repeat(2,minmax(10rem,0.6fr))]">
        <Card className={counts.pending > 0 ? 'border-warning/30 bg-warning-wash' : undefined}>
          <CardContent className="flex items-start justify-between gap-4">
            <div>
              <p className="text-warning text-sm font-semibold">Pending approval</p>
              <p className="text-ink numeric mt-2 text-4xl font-semibold">{counts.pending}</p>
              <p className="text-muted mt-1 text-xs">Unconfirmed accounts on this loaded page.</p>
            </div>
            <Clock3 aria-hidden="true" className="text-warning size-5" />
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <div className="text-success flex items-center gap-2 text-xs font-semibold">
              <UserRoundCheck aria-hidden="true" className="size-4" />
              Approved
            </div>
            <p className="text-ink numeric mt-2 text-2xl font-semibold">{counts.approved}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <div className="text-muted flex items-center gap-2 text-xs font-semibold">
              <UserRoundX aria-hidden="true" className="size-4" />
              Disabled
            </div>
            <p className="text-ink numeric mt-2 text-2xl font-semibold">{counts.disabled}</p>
          </CardContent>
        </Card>
      </section>

      <Card>
        <CardContent className="text-muted text-sm">
          Showing product SaaS users for <strong className="text-ink">{state.env}</strong>. Analytics operator membership is managed
          separately and is never changed here.
          {usersQuery.data?.paginationToken ? ' Counts describe this loaded page; more users are available.' : ''}
        </CardContent>
      </Card>

      <SearchInput onChange={(search) => setState({ search })} placeholder="Search email or account identity" value={state.search} />

      <Tabs
        onValueChange={(nextView) =>
          void navigate({ pathname: '/users', search: updateAccessView(location.search, nextView as AccessView) })
        }
        value={view}
      >
        <div className="overflow-x-auto pb-1">
          <TabsList>
            <TabsTrigger value="pending">Pending {counts.pending}</TabsTrigger>
            <TabsTrigger value="approved">Approved {counts.approved}</TabsTrigger>
            <TabsTrigger value="disabled">Disabled {counts.disabled}</TabsTrigger>
            <TabsTrigger value="all">All {counts.all}</TabsTrigger>
          </TabsList>
        </div>

        {accessViews.map((accessView) => (
          <TabsContent key={accessView} value={accessView}>
            <div className="grid min-w-0 gap-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-ink text-base font-semibold">{accessLabel(accessView)} accounts</h2>
                  <p className="text-muted mt-1 text-sm">One explicit action changes only SaaS access in {state.env}.</p>
                </div>
                <div className="flex gap-2">
                  {state.userPageToken ? (
                    <Button onClick={() => setState({ userPageToken: null })} variant="secondary">
                      First page
                    </Button>
                  ) : null}
                  {usersQuery.data?.paginationToken ? (
                    <Button onClick={() => setState({ userPageToken: usersQuery.data?.paginationToken ?? null })} variant="secondary">
                      Next page
                    </Button>
                  ) : null}
                </div>
              </div>
              {users.length === 0 ? (
                <EmptyState
                  title={
                    state.search
                      ? `No ${accessLabel(accessView).toLowerCase()} accounts match this search`
                      : `No ${accessLabel(accessView).toLowerCase()} accounts on this page`
                  }
                  tone={state.search ? 'filtered' : 'healthy'}
                >
                  {state.search
                    ? 'Clear the user search or choose another access state.'
                    : 'The loaded product-user page contains no accounts in this state.'}
                </EmptyState>
              ) : null}
              {users.length > 0 ? (
                <>
                  <div className="grid min-w-0 gap-3 md:hidden">
                    {users.map((user) => (
                      <article
                        className="border-line grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3 rounded-lg border p-4"
                        key={user.username}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-ink truncate font-semibold">{displayIdentity(user)}</p>
                            <p className="text-muted mt-1 text-xs">Registered {formatRelativeTime(user.createdAt)}</p>
                          </div>
                          <Badge tone={accessTone(accessState(user))}>{accessLabel(accessState(user))}</Badge>
                        </div>
                        <TechnicalIdentity user={user} />
                        <AccessAction
                          isPending={updateMutation.isPending}
                          onSelect={(selected, saasAccess) => setPendingAccessChange({ saasAccess, user: selected })}
                          user={user}
                        />
                      </article>
                    ))}
                  </div>
                  <TableShell className="hidden md:block">
                    <Table
                      aria-label={`${accessLabel(accessView)} SaaS user accounts`}
                      className="table-fixed"
                      style={{ minWidth: '56rem' }}
                    >
                      <TableHeader>
                        <TableRow>
                          <TableHead>Account</TableHead>
                          <TableHead className="w-36">State</TableHead>
                          <TableHead className="w-44">Registered</TableHead>
                          <TableHead className="w-48">Technical detail</TableHead>
                          <TableHead className="w-48 text-right">Access action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((user) => (
                          <TableRow key={user.username}>
                            <TableCell>
                              <p className="truncate font-semibold">{displayIdentity(user)}</p>
                              <p className="text-muted mt-1 text-xs">Product SaaS account</p>
                            </TableCell>
                            <TableCell>
                              <Badge tone={accessTone(accessState(user))}>{accessLabel(accessState(user))}</Badge>
                            </TableCell>
                            <TableCell>
                              <p title={formatTime(user.createdAt)}>{formatRelativeTime(user.createdAt)}</p>
                              <p className="text-muted mt-1 text-xs">{formatTime(user.createdAt)}</p>
                            </TableCell>
                            <TableCell>
                              <TechnicalIdentity user={user} />
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end">
                                <AccessAction
                                  isPending={updateMutation.isPending}
                                  onSelect={(selected, saasAccess) => setPendingAccessChange({ saasAccess, user: selected })}
                                  user={user}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableShell>
                </>
              ) : null}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
